import * as React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import './ConnectedAccount.scss';
import { ROUTE_PATH, UUID_REGEX } from '../../../constants';
import ConnectedAccountListView from './list/ConnectedAccountListView';
import ConnectedAccountAddNewView from './add-edit/ConnectedAccountAddNewView';
import ConnectedAccountEdit from './add-edit/ConnectedAccountEditView';
import { PageTitle } from '../../../widgets/PageTitle';
import ViewHeader from '../../../widgets/ViewHeader';
import Panel from '../../../widgets/Panel';
import LoadingIndicator from '../../../widgets/LoadingIndicator';
import Viewport from '../../../widgets/Viewport';
import { APIError } from '../../../APIError';
import ErrorJumbotron from '../../../widgets/ErrorJumbotron';
import { withTrashableContext } from '../../../hooks';
import { useFetchSchemasOnMount } from './useFetchSchemasOnMount';

export const LoadingOrErrorView: React.FunctionComponent<{
  error: APIError | null;
}> = ({ error }) => {
  return (
    <Viewport>
      <PageTitle title="Connected Accounts" />
      <ViewHeader title="Connected Accounts" />

      <Panel className="min-height-for-loading-indicator">
        {error ? (
          <ErrorJumbotron error={error} />
        ) : (
          <LoadingIndicator size="lg" centered>
            Loading available cloud applications...
          </LoadingIndicator>
        )}
      </Panel>
    </Viewport>
  );
};

export const ConnectedAccountRouteManager = () => {
  const { isLoading, schemas, error } = useFetchSchemasOnMount();

  const addNewPath = React.useMemo(() => {
    const types = schemas.map((schema) => schema.name).join('|');
    return `${ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.NEW}/:type(${types})?`;
  }, [schemas]);

  if (isLoading || schemas.length === 0 || error) {
    return <LoadingOrErrorView error={error} />;
  }

  return (
    <Switch>
      <Route
        exact
        path={ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.MAIN}
        component={ConnectedAccountListView}
      />

      <Route exact path={addNewPath} component={ConnectedAccountAddNewView} />

      <Route
        exact
        path={`${ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.EDIT}/:uuid(${UUID_REGEX})?`}
        component={ConnectedAccountEdit}
      />

      <Redirect to={ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.MAIN} />
    </Switch>
  );
};

export default withTrashableContext(ConnectedAccountRouteManager);
